<div
  class="eventq-data-table"
  [class.eventq-data-table--compact]="density() === 'compact'"
  [class.eventq-data-table--scrollable]="maxHeight() && !fillRemainingHeight()"
  [class.eventq-data-table--fill-remaining]="fillRemainingHeight()"
  [class.eventq-data-table--loading]="loading()"
  [class.eventq-data-table--fit-content]="fitsContent()"
  [class.eventq-data-table--wrap-rows]="hasWrapColumn()"
  [style.width]="fitsContent() ? 'max-content' : null"
  [style.min-width]="fitsContent() ? '100%' : null"
  [style.--eventq-data-table-columns]="gridTemplateColumns()"
  [style.--eventq-data-table-max-height]="maxHeight()"
>
  <div class="eventq-data-table__viewport">
    <div class="eventq-data-table__header">
      @for (column of columns(); track column.key) {
        <button
          type="button"
          class="eventq-data-table__header-cell"
          [class.eventq-data-table__header-cell--sortable]="column.sortable && !loading()"
          [class.eventq-data-table__header-cell--align-center]="cellAlign(column) === 'center'"
          [disabled]="loading() && !!column.sortable"
          (click)="onHeaderClick(column)"
        >
          <span class="eventq-data-table__header-label" [title]="column.label">{{ column.label }}</span>
          @if (sortIcon(column); as icon) {
            <mat-icon class="eventq-data-table__sort-icon">{{ icon }}</mat-icon>
          }
        </button>
      }
    </div>
    <div class="eventq-data-table__body" #tableBody (scroll)="onBodyScroll($event)">
      @if (loading()) {
        @for (index of skeletonIndexes(); track index) {
          <div class="eventq-data-table__skeleton-row" aria-hidden="true">
            @for (column of columns(); track column.key) {
              <span
                class="eventq-data-table__cell eventq-data-table__skeleton-cell"
                [class.eventq-data-table__cell--align-center]="cellAlign(column) === 'center'"
              >
                <span
                  class="eventq-data-table__skeleton-block"
                  [class.eventq-data-table__skeleton-block--icon]="
                    cellType(column) === 'indicator' ||
                    cellType(column) === 'flag' ||
                    cellType(column) === 'armStatus'
                  "
                  [class.eventq-data-table__skeleton-block--text]="
                    cellType(column) !== 'indicator' &&
                    cellType(column) !== 'flag' &&
                    cellType(column) !== 'armStatus'
                  "
                ></span>
              </span>
            }
          </div>
        }
      } @else {
        <div
          class="eventq-data-table__virtual-spacer"
          [class.eventq-data-table__virtual-spacer--active]="shouldVirtualize()"
          [style.height.px]="shouldVirtualize() ? virtualHeight() : null"
        >
          <div
            class="eventq-data-table__virtual-window"
            [style.transform]="shouldVirtualize() ? virtualTransform() : null"
          >
            @for (row of renderedRows(); track rowId(row)) {
              <ng-container
                [ngTemplateOutlet]="tableRow"
                [ngTemplateOutletContext]="{ $implicit: row }"
              />
            }
          </div>
        </div>
        @if (!shouldVirtualize() && isEmpty() && showLabelWhenEmpty()) {
          <div class="eventq-data-table__empty" role="status">
            @if (emptyIconSrc(); as iconSrc) {
              <img class="eventq-data-table__empty-icon" [src]="iconSrc | prefixUrl" alt="" aria-hidden="true" />
            }
            <span class="eventq-data-table__empty-label">{{ emptyLabel() }}</span>
          </div>
        } @else if (!shouldVirtualize() && fillRemainingHeight()) {
          <div class="eventq-data-table__body-filler" aria-hidden="true"></div>
        }
      }
    </div>
  </div>
</div>

<ng-template #tableRow let-row>
  <div
    class="eventq-data-table__row"
    [attr.data-row-id]="rowId(row)"
    [attr.role]="rowInteractive() && !isRowDisabled(row) ? 'button' : null"
    [attr.tabindex]="rowInteractive() && !isRowDisabled(row) ? 0 : null"
    [attr.aria-disabled]="isRowDisabled(row) ? 'true' : null"
    [class.eventq-data-table__row--disabled]="isRowDisabled(row)"
    [class.eventq-data-table__row--selected]="selectedRowId() === rowId(row)"
    (click)="onRowActivate(row)"
    (keydown.enter)="onRowActivate(row)"
    (keydown.space)="onRowSpace($event, row)"
  >
    @for (column of columns(); track column.key) {
              <span
                class="eventq-data-table__cell"
                [class.eventq-data-table__cell--align-center]="cellAlign(column) === 'center'"
                [class.eventq-data-table__cell--flag]="cellType(column) === 'flag'"
                [class.eventq-data-table__cell--link]="cellType(column) === 'link'"
                [class.eventq-data-table__cell--action]="cellType(column) === 'actionText'"
                [class.eventq-data-table__cell--wrap]="column.wrap"
              >
                @switch (cellType(column)) {
                  @case ('link') {
                    <button
                      type="button"
                      class="eventq-data-table__cell-link"
                      [title]="cellValue(row, column.key)"
                      (click)="onLinkCellClick($event, row, column)"
                    >
                      {{ cellValue(row, column.key) }}
                    </button>
                  }
                  @case ('indicator') {
                    @if (showIndicator(row, column)) {
                      <eventq-alarm-indicator [viewed]="indicatorActive(row, column.key)" />
                    }
                  }
                  @case ('aiTags') {
                    <span class="eventq-data-table__tags">
                      @for (tag of aiTags(row, column.key); track tagTrackKey(tag, $index)) {
                        <eventq-ai-event-tag [label]="tag.label" [variant]="tag.variant" />
                      }
                    </span>
                  }
                  @case ('flag') {
                    <eventq-flag-alarm-toggle
                      [flagged]="isFlagged(row, column.key)"
                      (click)="$event.stopPropagation()"
                      (flaggedChange)="onFlagToggle(row, column.key, $event)"
                    />
                  }
                  @case ('armStatus') {
                    @if (armStatusInteractive()) {
                      <button
                        type="button"
                        class="eventq-data-table__arm-status"
                        [attr.aria-label]="armStatusLabel(row, column.key)"
                        (click)="onArmStatusClick($event, row)"
                      >
                        <mat-icon
                          class="eventq-data-table__arm-status-icon"
                          [class.eventq-data-table__arm-status-icon--armed]="
                            armStatus(row, column.key) === 'armed'
                          "
                          [class.eventq-data-table__arm-status-icon--disarmed]="
                            armStatus(row, column.key) === 'disarmed'
                          "
                          aria-hidden="true"
                        >{{ armStatusIcon(row, column.key) }}</mat-icon>
                      </button>
                    } @else {
                      <span
                        class="eventq-data-table__arm-status eventq-data-table__arm-status--static"
                        role="img"
                        [attr.aria-label]="armStatusLabel(row, column.key)"
                      >
                        <mat-icon
                          class="eventq-data-table__arm-status-icon"
                          [class.eventq-data-table__arm-status-icon--armed]="
                            armStatus(row, column.key) === 'armed'
                          "
                          [class.eventq-data-table__arm-status-icon--disarmed]="
                            armStatus(row, column.key) === 'disarmed'
                          "
                          aria-hidden="true"
                        >{{ armStatusIcon(row, column.key) }}</mat-icon>
                      </span>
                    }
                  }
                  @case ('actionText') {
                    <button
                      type="button"
                      class="eventq-data-table__cell-action"
                      [title]="cellValue(row, column.key)"
                      (click)="onCellAction($event, row, column)"
                    >
                      <span class="eventq-data-table__cell-text">{{
                        cellValue(row, column.key)
                      }}</span>
                    </button>
                  }
                  @default {
                    @if (column.indicatorKey && isFlagged(row, column.indicatorKey)) {
                      <eventq-alarm-indicator [viewed]="false" />
                    }
                    @if (cellValue(row, column.key); as text) {
                      <span class="eventq-data-table__cell-text" [title]="text">{{ text }}</span>
                    }
                  }
                }
              </span>
            }
  </div>
</ng-template>
